/**
 * ApiKeysStep — first onboarding step (Sprint 1.1).
 *
 * The system requires the user's own LLM API key to do anything (positioning
 * suggest, transcription, copy generation, agent reasoning). Front-loading
 * the collection avoids the dead-end where a user finishes onboarding and
 * then sees every AI button blocked.
 *
 * Current UX contract:
 *  - OpenAI is the only visible and required provider.
 *  - Legacy providers stay defined for reversible multi-provider mode.
 *  - A usable OpenAI key is required to continue (`onValidityChange`).
 */

import { useEffect, useMemo } from "react";
import {
  Brain,
  CheckCircle,
  GoogleLogo,
  OpenAiLogo,
  Sparkle,
  WarningCircle,
} from "@phosphor-icons/react";
import { StepContainer } from "../components/StepContainer";
import { AddKeyForm } from "@/features/byok/components/AddKeyForm";
import { useByokKeys } from "@/features/byok/hooks";
import { useSyncByokToken } from "@/features/byok/hooks/useSyncByokToken";
import type { ByokKey, ProviderId } from "@/features/byok/types";
import { STEPS, findStepIndexBySlug } from "../config/steps";
import { OPENAI_ONLY_MODE } from "@/lib/ai/providerPolicy";

interface ProviderConfig {
  id: ProviderId;
  label: string;
  tagline: string;
  placeholder: string;
  link: string;
  Icon: typeof Brain;
  required: boolean;
}

// Legacy provider definitions stay in code for reversible multi-provider mode.
const PROVIDERS: ProviderConfig[] = [
  {
    id: "openai",
    label: "OpenAI",
    tagline: "Obrigatória. Transcrição, análise e geração usam sua chave OpenAI.",
    placeholder: "sk-...",
    link: "https://platform.openai.com/api-keys",
    Icon: OpenAiLogo,
    required: true,
  },
  {
    id: "anthropic",
    label: "Anthropic",
    tagline: "Recomendada. Produz a melhor copy para posicionamento e estratégia.",
    placeholder: "sk-ant-...",
    link: "https://console.anthropic.com/settings/keys",
    Icon: Brain,
    required: false,
  },
  {
    id: "google",
    label: "Google (Gemini)",
    tagline: "Recomendada. Entra como fallback robusto e mantém o sistema vivo se outros caírem.",
    placeholder: "Cole sua chave (AIza… ou AQ…)",
    link: "https://aistudio.google.com/app/apikey",
    Icon: GoogleLogo,
    required: false,
  },
];

const VISIBLE_PROVIDERS = OPENAI_ONLY_MODE
  ? PROVIDERS.filter((provider) => provider.id === "openai")
  : PROVIDERS;

function isUsable(k: ByokKey): boolean {
  return k.status === "valid" || k.status === "pending" || k.status === "rate_limited";
}

export interface ApiKeysStepProps {
  onValidityChange: (valid: boolean) => void;
  onAttemptContinue: (continueFn: () => Promise<boolean>) => void;
  onMissingChange?: (labels: string[]) => void;
}

export function ApiKeysStep({
  onValidityChange,
  onAttemptContinue,
  onMissingChange,
}: ApiKeysStepProps) {
  const step = STEPS[findStepIndexBySlug("conexoes")];
  // Inject Clerk session token into the BYOK fetch client. ApiKeysSection
  // calls this on /perfil; the wizard step needs the same plumbing or
  // every byok-keys call 401s with "No active session".
  useSyncByokToken();
  const { data: keys, isLoading } = useByokKeys();

  const status = useMemo(() => {
    const list = keys ?? [];
    return VISIBLE_PROVIDERS.map((p) => ({
      ...p,
      key: list.find((k) => k.provider === p.id) ?? null,
      hasUsable: list.some((k) => k.provider === p.id && isUsable(k)),
    }));
  }, [keys]);

  const openaiOk = status.find((s) => s.id === "openai")?.hasUsable ?? false;
  const canContinue = openaiOk;

  useEffect(() => {
    onValidityChange(canContinue);
    if (onMissingChange) {
      onMissingChange(canContinue ? [] : ["chave OpenAI (obrigatória)"]);
    }
  }, [canContinue, onValidityChange, onMissingChange]);

  useEffect(() => {
    onAttemptContinue(async () => canContinue);
  }, [canContinue, onAttemptContinue]);

  return (
    <StepContainer
      icon={step.icon}
      headline={step.headline}
      sub={step.sub}
      importanceNote="Sua chave fica criptografada no servidor. Nunca cobramos pelos tokens — você paga direto no provedor escolhido."
    >
      <div className="flex flex-col gap-4">
        <div className="rounded-md border border-primary/20 bg-primary/[0.04] p-3 text-sm">
          <p className="font-medium text-foreground">
            Qual chave cadastrar?
          </p>
          <p className="mt-1 text-muted-foreground">
            <strong className="text-foreground">Cadastre a OpenAI.</strong> Ela é usada
            para texto, análise, visão e transcrição. GPT-5.6 Luna, Terra e Sol são
            roteados automaticamente conforme cada função.
          </p>
        </div>

        <div
          className={
            "rounded-md border px-3 py-2 text-sm " +
            (!canContinue
              ? "border-rose-500/30 bg-rose-500/[0.06] text-rose-200"
              : "border-emerald-500/20 bg-emerald-500/[0.04] text-emerald-200/90")
          }
        >
          {isLoading ? (
            "Verificando chaves..."
          ) : !canContinue ? (
            <>
              <WarningCircle weight="fill" className="-mt-0.5 mr-1 inline size-4" />
              Cadastre a chave OpenAI (obrigatória) para continuar.
            </>
          ) : (
            <>
              <CheckCircle weight="fill" className="-mt-0.5 mr-1 inline size-4" />
              OpenAI conectada. Modelos GPT-5.6 prontos para uso.
            </>
          )}
        </div>

        <ol className="flex flex-col gap-3">
          {status.map((p) => (
            <li
              key={p.id}
              className={
                "rounded-xl border p-4 transition-colors " +
                (p.hasUsable
                  ? "border-emerald-500/30 bg-emerald-500/[0.04]"
                  : p.required
                    ? "border-rose-500/30 bg-rose-500/[0.04]"
                    : "border-border/60 bg-card/40")
              }
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <p.Icon className="size-5" weight="duotone" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      {p.required ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-rose-500/15 px-2 py-0.5 text-xs font-medium text-rose-300">
                          Obrigatória
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 rounded-full bg-sky-500/15 px-2 py-0.5 text-xs font-medium text-sky-300">
                          Recomendada
                        </span>
                      )}
                      {p.hasUsable ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300">
                          <CheckCircle weight="fill" className="size-3" /> Conectada
                        </span>
                      ) : null}
                    </div>
                    <h3 className="mt-0.5 text-base font-semibold text-foreground">
                      {p.label}
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">{p.tagline}</p>
                    <a
                      href={p.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1 inline-block text-xs text-primary underline-offset-2 hover:underline"
                    >
                      Onde pegar minha chave →
                    </a>
                  </div>
                </div>
              </div>

              {!p.hasUsable ? (
                <div className="mt-4">
                  <AddKeyForm provider={p.id} placeholder={p.placeholder} />
                </div>
              ) : (
                <p className="mt-3 text-xs text-muted-foreground">
                  Última terminação: <code>•••{p.key?.last4 ?? ""}</code> · você pode
                  trocar depois em Perfil → API Keys.
                </p>
              )}
            </li>
          ))}
        </ol>

        <p className="flex items-start gap-1.5 text-xs italic text-muted-foreground">
          <Sparkle className="mt-0.5 size-3.5 shrink-0" />
          Você só paga ao provedor — nada passa pelo nosso servidor além da chave
          criptografada e dos prompts do recurso que você usar.
        </p>
      </div>
    </StepContainer>
  );
}
